/**
 * Badge — ID Visual V2 status badge component
 */
import type { JSX } from 'preact';
import { T, CT } from '../../styles/tokens';

type BadgeVariant = 'success' | 'warning' | 'error' | 'info' | 'neutral';

const VARIANTS: Record<BadgeVariant, { color: string; bg: string; border: string }> = {
  success: { color: T.success, bg: T.successBg, border: T.successBg },
  warning: { color: T.warning, bg: T.warningBg, border: T.warningBg },
  error:   { color: T.danger, bg: T.dangerBg, border: T.dangerBg },
  info:    { color: T.primary, bg: T.primaryBg, border: T.primaryBg },
  neutral: { color: T.textSecondary, bg: T.bgHover, border: T.borderHover },
};

interface BadgeProps {
  children: any;
  variant?: BadgeVariant;
  color?: string;
  dot?: boolean;
  pulseDot?: boolean;
  style?: JSX.CSSProperties;
}

export function Badge({ children, variant = 'neutral', color, dot, pulseDot, style }: BadgeProps) {
  const v = VARIANTS[variant];
  const c = color || v.color;
  const bg = color ? `${color}18` : v.bg;
  const border = color ? `${color}30` : v.border;

  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 5,
      padding: CT.badge.padding, borderRadius: CT.badge.radius,
      fontSize: CT.badge.fontSize, fontWeight: CT.badge.fontWeight, textTransform: 'uppercase',
      color: c, background: bg, border: `1px solid ${border}`,
      ...style,
    }}>
      {dot && (
        <span style={{
          width: 7, height: 7, borderRadius: '50%', background: c, flexShrink: 0,
          ...(pulseDot ? { animation: 'pulse 2s infinite' } : {}),
        }} />
      )}
      {children}
    </span>
  );
}
